import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  SnackbarContent,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Check } from '@material-ui/icons';
import React, { useState } from 'react';
import styled from 'styled-components';

import useMaestroExport from 'core/hooks/useMaestroExport';
import useVehicleMaintenances from 'core/hooks/useVehicleMaintenances';
import { Maintenance } from 'core/types/maintenance';
import { useSchemaMeta } from 'Elements/Form/Schema';
import { datetime, Thousands } from 'Elements/Formats';
import {
  Body,
  Column,
  GridTable,
  Header,
  LinkRow,
  Row as GridRow,
} from 'Elements/GridTable';
import LeadingRow from 'Elements/GridTable/LeadingRow';

import Location from '../shared/Location';
import MaintenanceStatus from '../shared/MaintenanceStatus';

const AbsolutelyPositionedSnackbarContent = styled(SnackbarContent)`
  position: absolute;
  right: 20px;
  bottom: 20px;
`;

const Row = styled(GridRow)`
  grid-template-columns: 130px 150px 1fr 130px 120px;
  border-bottom: none;

  ${Column}:nth-child(5) {
    text-align: right;
  }
`;

const Spinner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CenterColumn = styled(Column)`
  grid-column: 1 / 6;
  display: flex;
  justify-content: center;
`;

export default function VehicleMaintenancesTable() {
  const [selectedForExport, setSelectedForExport] = useState({});
  const [exported, setExported] = useState<any[]>([]);
  const { exportMaintenance } = useMaestroExport();
  const [loader, setLoading] = useState<number>(0);

  const { vehicleId } = useSchemaMeta<{ vehicleId: number }>();
  const { data, loading, hasMore, loadMore } = useVehicleMaintenances(
    vehicleId
  );

  const exportToMaestro = () => {
    Object.keys(selectedForExport).map(maintenanceId => {
      setLoading(loader => loader + 1);

      exportMaintenance(maintenanceId)
        .then(exportedMaintenance => {
          if (exportedMaintenance) {
            setExported(exported => [...exported, exportedMaintenance]);
            setSelectedForExport(currentState => ({
              ...Object.keys(currentState)
                .filter(key => key === exportedMaintenance.id)
                .reduce(
                  (res, key) =>
                    Object.assign(res, { [key]: currentState[key] }),
                  {}
                )
            }));
          }
        })
        .finally(() => setLoading(loader => loader - 1));
      return maintenanceId;
    });
  };

  const handleChange = (id: number) => (event: any) => {
    let checkedMaintenances = {
      ...selectedForExport,
      [id]: event.target.checked
    };

    setSelectedForExport({
      ...Object.keys(checkedMaintenances)
        .filter(key => checkedMaintenances[key] === true)
        .reduce(
          (res, key) => Object.assign(res, { [key]: checkedMaintenances[key] }),
          {}
        )
    });
  };

  const isExported = (maintenance: Maintenance) => {
    return (
      maintenance.external_id ||
      exported.findIndex(e => e.id === maintenance.id) !== -1
    );
  };

  return (
    <Card elevation={1}>
      <CardHeader title="Huollot" />
      <GridTable>
        <Header>
          <LeadingRow>
            <Column></Column>
            <Row>
              {Object.keys(selectedForExport).length > 0 && (
                <AbsolutelyPositionedSnackbarContent
                  message={
                    <>Valittu {Object.keys(selectedForExport).length} huoltoa</>
                  }
                  action={
                    <Button
                      color="secondary"
                      onClick={exportToMaestro}
                      disabled={loader !== 0}
                    >
                      {loader !== 0 && (
                        <Spinner>
                          <CircularProgress size={24} />
                        </Spinner>
                      )}
                      Vie Maestroon
                    </Button>
                  }
                ></AbsolutelyPositionedSnackbarContent>
              )}
              <Column>Päivämäärä</Column>
              <Column>Paikka</Column>
              <Column>Työntekijä</Column>
              <Column>Tila</Column>
              <Column>Kilometrit</Column>
            </Row>
          </LeadingRow>
        </Header>

        <Body>
          {data.map(maintenance => (
            <LeadingRow key={maintenance.id} hover>
              <Column>
                {!isExported(maintenance) && (
                  <Tooltip
                    title="Valitse viedäksesi Maestroon"
                    placement="right"
                  >
                    <Checkbox
                      color="secondary"
                      onChange={handleChange(maintenance.id)}
                    />
                  </Tooltip>
                )}
                {isExported(maintenance) && (
                  <Tooltip title="Viety Maestroon" placement="right">
                    <div>
                      <Checkbox disabled icon={<Check color="secondary" />} />
                    </div>
                  </Tooltip>
                )}
              </Column>

              <LinkRow to={`/maintenances/${maintenance.id}`}>
                <Row>
                  <Column>
                    {datetime.format(new Date(maintenance.created_at))}
                  </Column>
                  <Column>
                    <Location id={maintenance.location_id} />
                  </Column>
                  <Column>
                    {maintenance.employee
                      ? maintenance.employee.full_name
                      : '–'}
                  </Column>
                  <Column>
                    <MaintenanceStatus id={maintenance.maintenance_status_id} />
                  </Column>
                  <Column>
                    <Thousands>{maintenance.kilometer_increment}</Thousands>
                  </Column>
                </Row>
              </LinkRow>
            </LeadingRow>
          ))}

          <Row>
            <CenterColumn>
              {loading && <CircularProgress />}

              {!loading && hasMore && (
                <Button variant="contained" color="primary" onClick={loadMore}>
                  Lataa lisää
                </Button>
              )}

              {!loading && !hasMore && (
                <Typography variant="subtitle1">Ei enempää huoltoja</Typography>
              )}
            </CenterColumn>
          </Row>
        </Body>
      </GridTable>
    </Card>
  );
}
